import { InputHTMLAttributes, useCallback, useEffect, useRef, useState } from 'react'

type DeferredNumberInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'defaultValue' | 'onChange' | 'onBlur'
> & {
  value: number
  onCommit: (value: number) => number
  commitDelay?: number
}

const DeferredNumberInput = ({
  value,
  onCommit,
  commitDelay = 1000,
  onFocus,
  onKeyDown,
  ...inputProps
}: DeferredNumberInputProps) => {
  const [draftValue, setDraftValue] = useState(String(value))
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearCommitTimer = useCallback(() => {
    if (!commitTimer.current) return
    clearTimeout(commitTimer.current)
    commitTimer.current = null
  }, [])

  const commit = useCallback((draft: string) => {
    clearCommitTimer()
    const parsedValue = Number(draft)
    const committedValue = onCommit(Number.isFinite(parsedValue) ? parsedValue : value)
    setDraftValue(String(committedValue))
  }, [clearCommitTimer, onCommit, value])

  useEffect(() => {
    setDraftValue(String(value))
  }, [value])

  useEffect(() => clearCommitTimer, [clearCommitTimer])

  return (
    <input
      {...inputProps}
      type="number"
      value={draftValue}
      onChange={(event) => {
        const nextDraft = event.target.value
        setDraftValue(nextDraft)
        clearCommitTimer()
        commitTimer.current = setTimeout(() => commit(nextDraft), commitDelay)
      }}
      onBlur={() => commit(draftValue)}
      onFocus={onFocus}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur()
        onKeyDown?.(event)
      }}
    />
  )
}

export default DeferredNumberInput
